import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { servicesAPI, serviceRequestsAPI } from '@/lib/api';
import { FileText, Building, Calculator, AlertCircle, CheckCircle } from 'lucide-react';

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const navigate = useNavigate();

  const loadServices = useCallback(async () => {
    try {
      const response = await servicesAPI.getAll();
      setServices(response.data);
    } catch (err) {
      console.error('Error loading services:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const handleSelectService = async (service) => {
    try {
      setSelectedService(service);
      const response = await serviceRequestsAPI.create({ service_id: service.id });
      
      // Show success message
      setShowSuccess(true);
      
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      console.error('Error creating service request:', err);
      alert('Failed to select service. Please try again.');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'income_tax':
        return <Calculator className="text-blue-600" size={24} />;
      case 'gst':
        return <FileText className="text-green-600" size={24} />;
      case 'business':
        return <Building className="text-purple-600" size={24} />;
      case 'consultation':
        return <Calculator className="text-orange-600" size={24} />;
      default:
        return <FileText className="text-gray-600" size={24} />;
    }
  };

  const categories = [
    { id: 'all', name: 'All Services' },
    { id: 'income_tax', name: 'Income Tax' },
    { id: 'gst', name: 'GST' },
    { id: 'business', name: 'Business' },
    { id: 'consultation', name: 'Consultation' },
  ];

  const filteredServices = selectedCategory === 'all' 
    ? services 
    : services.filter(s => s.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Success Message Modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="success-modal">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle className="text-green-600" size={40} />
            </div>
            <h3 className="text-2xl font-bold mb-2 text-gray-900">Service Added!</h3>
            <p className="text-gray-600 mb-4">
              {selectedService?.name} has been added to your dashboard.
            </p>
            <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
          </div>
        </div>
      )}
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" data-testid="services-page-title">Our Services</h1>
          <p className="text-gray-600">Choose the service you need and get started</p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-8" data-testid="category-filter">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-6 py-2 rounded-full font-medium transition ${
                selectedCategory === cat.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
              data-testid={`category-${cat.id}`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredServices.map((service) => (
            <div 
              key={service.id} 
              className="bg-white rounded-xl shadow-sm hover:shadow-md transition p-6 border border-gray-200"
              data-testid={`service-card-${service.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  {getCategoryIcon(service.category)}
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">₹{service.price.toLocaleString()}</div>
                  {service.price >= 999 && service.name.includes('Filing') && service.category === 'gst' && (
                    <div className="text-xs text-gray-500">per month</div>
                  )}
                </div>
              </div>

              <h3 className="text-xl font-semibold mb-2">{service.name}</h3>
              <p className="text-gray-600 mb-4">{service.description}</p>

              <div className="mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2">Required Documents:</p>
                <ul className="space-y-1">
                  {service.required_documents.slice(0, 3).map((doc) => (
                    <li key={doc} className="text-sm text-gray-600 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                      {doc}
                    </li>
                  ))}
                  {service.required_documents.length > 3 && (
                    <li className="text-sm text-gray-500">+{service.required_documents.length - 3} more</li>
                  )}
                </ul>
              </div>

              <button
                onClick={() => handleSelectService(service)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition"
                data-testid={`select-service-${service.id}`}
              >
                Select Service
              </button>
            </div>
          ))}
        </div>

        {filteredServices.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-600">No services found in this category</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServicesPage;